import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  Building2, Users, FileText, Clock, DollarSign, Star, 
  UserPlus, ChevronRight, Settings, BarChart3, Loader2,
  Briefcase, TrendingUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function VendorDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/vendor/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteCaregiver = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;

    setInviting(true);
    try {
      await api.post('/vendor/caregivers/invite', { email: inviteEmail });
      toast.success('Invitation sent!');
      setInviteEmail('');
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Vendor Portal</h1>
              <p className="text-xs sm:text-sm text-slate-500">{dashboard?.profile?.company_name || user?.name}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Caregivers</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_caregivers || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Contracts</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.active_contracts || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Hours This Month</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_hours_this_month || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Rating</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.rating?.toFixed(1) || '—'}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Hours Alert */}
        {stats.pending_hours > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">{stats.pending_hours} hours pending approval</p>
                <p className="text-sm text-amber-600">Review and submit for payment</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="caregivers" className="space-y-4">
          <TabsList>
            <TabsTrigger value="caregivers">Caregivers</TabsTrigger>
            <TabsTrigger value="contracts">Contracts</TabsTrigger>
            <TabsTrigger value="shifts">Recent Shifts</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
          </TabsList>

          <TabsContent value="caregivers" className="space-y-4">
            {/* Invite Caregiver */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-teal-500" />
                  Invite Caregiver
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInviteCaregiver} className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="caregiver@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="flex-1"
                    data-testid="invite-email-input"
                  />
                  <Button type="submit" disabled={inviting}>
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invite'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Caregiver List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Caregivers</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.caregivers?.length || 0) === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No caregivers yet</p>
                    <p className="text-sm">Invite caregivers to join your roster</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.caregivers.map((cg) => (
                      <div key={cg.user_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center overflow-hidden">
                            {cg.picture ? (
                              <img src={cg.picture} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-lg font-bold text-teal-700">{cg.name?.charAt(0) || '?'}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{cg.name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{cg.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {cg.verified && <Badge className="bg-green-100 text-green-700">Verified</Badge>}
                          <Badge variant="secondary">{cg.total_hours || 0} hrs</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contracts">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Active Contracts</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.contracts?.length || 0) === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No active contracts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.contracts.map((contract) => (
                      <div key={contract.contract_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{contract.client_name || 'Client'}</p>
                            <p className="text-sm text-slate-500">{contract.service_type}</p>
                          </div>
                          <Badge>{contract.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="shifts">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Shifts</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_shifts?.length || 0) === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No recent shifts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dashboard.recent_shifts.map((shift) => (
                      <div key={shift.shift_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{shift.caregiver_name || shift.caregiver_id}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(shift.clock_in).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{shift.total_hours?.toFixed(1) || '—'} hrs</p>
                          <Badge variant={shift.status === 'approved' ? 'default' : 'secondary'}>
                            {shift.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Invoices</CardTitle>
                <Button size="sm">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No invoices yet</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
